import * as THREE from 'three';
import { bus as eventBus } from '../core/EventBus';
import { AudioEngine, type BusLevels } from './AudioEngine';
import { AudioEvents } from './Events';
import { EngineVoice } from './EngineVoice';
import { RwrVoice } from './RwrVoice';
import type { RwrContact } from '../hud/HudFrame';
import type { Terrain } from '../world/Terrain';

/**
 * The soundscape: everything the game hears, in one place (SPEC §3.11).
 *
 * Owns the engine, the voices, and the subscriptions that turn game events into sound. Nothing
 * else in the codebase knows Web Audio exists, which keeps audio out of the simulation entirely —
 * it is presentation, it runs on the render clock, and it must never be able to affect a checksum
 * (CLAUDE.md determinism).
 *
 * Every entry point is safe before the context is unlocked, so the game is fully playable with the
 * audio system asleep.
 */

const listenerScratch = new THREE.Vector3();

export class Soundscape {
  readonly engine = new AudioEngine();

  private readonly voice = new EngineVoice(this.engine);
  private readonly rwr = new RwrVoice(this.engine);
  private readonly events = new AudioEvents(this.engine);

  private readonly detach: (() => void)[] = [];
  private terrain: Terrain | null = null;
  private muted = false;

  /** The listener is the aircraft; updated each frame so events can measure distance. */
  private readonly listenerM = new THREE.Vector3();
  private readonly listenerVelocityMs = new THREE.Vector3();

  constructor() {
    this.subscribe();
  }

  /** Terrain is optional: without it, nothing is occluded rather than everything. */
  setTerrain(terrain: Terrain): void {
    this.terrain = terrain;
  }

  /**
   * Unlock on a user gesture (SPEC's M9 note on the autoplay policy).
   *
   * Wired to several gestures rather than one, because browsers disagree about which count and a
   * game whose audio never starts is indistinguishable from one with no audio at all.
   */
  attachGestureUnlock(target: HTMLElement | Window): void {
    const unlock = (): void => {
      this.engine.unlock();
      this.voice.start();
      this.rwr.start();
    };
    const options = { passive: true } as const;
    for (const type of ['pointerdown', 'keydown', 'touchstart'] as const) {
      target.addEventListener(type, unlock, options);
      this.detach.push(() => target.removeEventListener(type, unlock));
    }
  }

  private subscribe(): void {
    this.detach.push(
      eventBus.on('threat:launch', (launch) => {
        this.events.launch(this.spatialFor(launch.originM));
      }),
    );

    this.detach.push(
      eventBus.on('missile:detonated', (hit) => {
        const spatial = this.spatialFor(hit.positionM);
        // A near miss is a flyby first and a detonation second — the sweep is what the player
        // remembers, so it is scheduled even when the warhead did no damage.
        this.events.flyby({ ...spatial, closingMs: 300 });
        this.events.detonation(spatial, hit.damage > 0 ? 1 : 0.5);
        if (hit.damage > 0) this.events.impact(Math.min(1, hit.damage * 4));
      }),
    );

    this.detach.push(
      eventBus.on('missile:expired', (miss) => {
        this.events.detonation(this.spatialFor(miss.positionM), 0.4);
      }),
    );

    this.detach.push(
      eventBus.on('target:impact', (hit) => {
        this.events.detonation(this.spatialFor(hit.positionM), 1);
      }),
    );
  }

  /** Distance, closure and occlusion from the listener to a world point. */
  private spatialFor(positionM: THREE.Vector3): {
    distanceM: number;
    closingMs: number;
    occluded: boolean;
  } {
    listenerScratch.copy(positionM).sub(this.listenerM);
    const distanceM = listenerScratch.length();
    let closingMs = 0;
    if (distanceM > 1e-3) {
      listenerScratch.multiplyScalar(1 / distanceM);
      // Positive when the gap is closing: the listener moving toward the source.
      closingMs = this.listenerVelocityMs.dot(listenerScratch);
    }
    const occluded =
      this.terrain !== null && !this.terrain.lineOfSight(this.listenerM, positionM);
    return { distanceM, closingMs, occluded };
  }

  /**
   * Drive the continuous layers. Called once per rendered frame, never from the fixed tick.
   *
   * `dtS` is the render delta, so the RWR rhythm runs on wall-clock time and stays steady through
   * a slow-motion debug step — a warning tone that speeds up with the time scale would be a lie.
   */
  update(
    dtS: number,
    state: {
      positionM: THREE.Vector3;
      velocityMs: THREE.Vector3;
      throttle: number;
      airspeedMs: number;
      stallDepth: number;
      loadFactor: number;
      engineRunning: boolean;
    },
    contacts: readonly RwrContact[],
    missileInbound: boolean,
  ): void {
    if (!this.engine.running) return;
    this.listenerM.copy(state.positionM);
    this.listenerVelocityMs.copy(state.velocityMs);

    if (this.muted) {
      this.voice.mute();
      this.rwr.mute();
      return;
    }

    this.voice.update(state);
    this.rwr.update(dtS, worstContactState(contacts, missileInbound));
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) {
      this.voice.mute();
      this.rwr.mute();
    }
  }

  setLevel(bus: keyof BusLevels, value: number): void {
    this.engine.setLevel(bus, value);
  }

  levels(): Readonly<BusLevels> {
    return this.engine.getLevels();
  }

  dispose(): void {
    for (let i = 0; i < this.detach.length; i += 1) this.detach[i]();
    this.detach.length = 0;
    this.voice.dispose();
    this.rwr.dispose();
    this.engine.dispose();
  }
}

/**
 * The single tone the RWR should be playing.
 *
 * Severity order, not sum: an inbound missile outranks everything on the ring, and a track
 * outranks a search. Playing all of them at once would bury the one that matters under the two
 * that do not.
 */
export function worstContactState(
  contacts: readonly RwrContact[],
  missileInbound: boolean,
): 'none' | 'search' | 'track' | 'launch' {
  if (missileInbound) return 'launch';
  let worst: 'none' | 'search' | 'track' | 'launch' = 'none';
  for (let i = 0; i < contacts.length; i += 1) {
    const state = contacts[i].state;
    if (state === 'launch') return 'launch';
    if (state === 'track') worst = 'track';
    else if (state === 'search' && worst === 'none') worst = 'search';
  }
  return worst;
}
